import { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { trackLogin, trackSignup, getAuthErrorMessage, saveUserToFirestore } from '../../utils';
import { findExistingAccountByEmail, addEmailAuthToOAuthAccount, isEmailVerificationRequired } from '../../utils/auth/accountMerger';
import { useTranslation } from 'react-i18next';
import { AI_API_URL } from '../../constants';
import { Capacitor } from '@capacitor/core';

/**
 * 커스텀 이메일 인증 관련 로직을 담당하는 훅
 * @returns {object} - 커스텀 이메일 인증 관련 상태와 함수들
 */
export const useCustomEmailAuth = () => {
  const { i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [authError, setAuthError] = useState('');

  /**
   * 커스텀 이메일 인증 발송
   */
  const sendCustomVerificationEmail = async (userEmail, displayName) => {
    try {
      console.log('📧 커스텀 이메일 인증 발송 시작:', { userEmail, displayName });
      
      const response = await fetch(`${AI_API_URL}/api/email-auth/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          displayName: displayName || '사용자',
          language: i18n.language || 'ko'
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ 커스텀 이메일 인증 발송 성공:', data);
        return true;
      } else {
        console.error('❌ 커스텀 이메일 인증 발송 실패:', data);
        return false;
      }
    } catch (error) {
      console.error('❌ 커스텀 이메일 인증 API 호출 실패:', error);
      return false;
    }
  };

  /**
   * 커스텀 이메일 인증 재발송
   */
  const resendCustomVerificationEmail = async (userEmail, displayName) => {
    try {
      setIsResending(true);
      console.log('🔄 커스텀 이메일 재발송 시작:', { userEmail, displayName });
      
      const response = await fetch(`${AI_API_URL}/api/email-auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          displayName: displayName || '사용자',
          language: i18n.language || 'ko'
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log('✅ 커스텀 이메일 인증 재발송 성공:', data);
        return true;
      } else {
        console.error('❌ 커스텀 이메일 인증 재발송 실패:', data);
        return false;
      }
    } catch (error) {
      console.error('❌ 커스텀 이메일 인증 재발송 API 호출 실패:', error);
      return false;
    } finally {
      setIsResending(false);
    }
  };

  /**
   * 이메일 회원가입/로그인 처리 함수
   * @returns {Promise<boolean>} - 성공 여부
   */
  const handleCustomEmailAuth = async () => {
    try {
      setAuthLoading(true);
      
      // ✅ Step 3: 플랫폼별 디버깅 정보 로깅
      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();
      
      if (isSignup) {
        // ✅ 회원가입 시작 전에 기존 계정 확인
        console.log('🔍 기존 계정 확인 중...', { email });
        const existingAccount = await findExistingAccountByEmail(email);
        
        if (existingAccount) {
          const currentLoginMethods = Array.isArray(existingAccount.loginMethod) 
            ? existingAccount.loginMethod 
            : [existingAccount.loginMethod];
          
          // OAuth 계정이면서 이메일 로그인이 없는 경우
          if (!currentLoginMethods.includes('email') && 
              currentLoginMethods.some(method => ['kakao', 'google', 'naver'].includes(method))) {
            
            console.log('✅ OAuth 계정에 이메일 로그인 추가 시도');
            
            // OAuth 계정에 이메일 로그인 추가
            const linkResult = await addEmailAuthToOAuthAccount(existingAccount, password, email);
            
            if (linkResult && linkResult.success) {
              console.log('✅ 계정 연동 완료, 자동 로그인 시도');
              
              // 연동된 계정으로 이메일/비밀번호 로그인
              const userCredential = await signInWithEmailAndPassword(auth, email, password);
              
              // 일반 회원가입과 동일한 처리
              await saveUserToFirestore(userCredential.user, {
                email: email,
                loginMethod: 'email'
              });
              
              setAuthLoading(false);
              
              const providerName = currentLoginMethods.find(method => 
                ['kakao', 'google', 'naver'].includes(method)
              );
              
              // 계정 연동 성공 이벤트 발생 (LandingLayout에서 처리)
              window.dispatchEvent(new CustomEvent('signup-success', {
                detail: { 
                  isAccountLinked: true,
                  provider: providerName
                }
              }));
              
              // 폼 초기화
              resetEmailForm();
              
              // Google Analytics 이벤트 추적
              trackLogin('email');
              trackSignup('email_linked');
              return true;
            } else {
              throw new Error('계정 연동에 실패했습니다.');
            }
          }
        }
        
        // 일반 회원가입 진행
        console.log('👤 [DEBUG] Firebase 회원가입 시도...', { platform });
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        if (userCredential.user) {
          console.log('✅ [DEBUG] Firebase 회원가입 성공:', userCredential.user.uid);
          
          // ✅ Step 3: 모든 플랫폼에서 커스텀 이메일 사용
          console.log(`📧 [DEBUG] ${isNative ? '네이티브' : '웹'} 환경: 커스텀 이메일 발송 사용`);
          const emailSent = await sendCustomVerificationEmail(
            email, 
            userCredential.user.displayName || '사용자'
          );
          
          if (emailSent) {
            console.log('✅ [DEBUG] 커스텀 이메일 인증 발송 성공');
          } else {
            console.error('❌ [DEBUG] 커스텀 이메일 인증 발송 실패');
          }
          
          if (emailSent) {
            // 회원가입 시 추가 정보와 함께 저장
            await saveUserToFirestore(userCredential.user, {
              email: email,
              loginMethod: 'email',
              signupMethod: 'email',
              platform: platform,
              customEmailVerification: true // 모든 플랫폼에서 커스텀 이메일 사용
            });
            
            console.log('✅ [DEBUG] 사용자 정보 Firestore 저장 완료');
            setAuthLoading(false);
            
            // 회원가입 성공 시 전역 이벤트 발생
            window.dispatchEvent(new CustomEvent('signup-success', {
              detail: { isAccountLinked: false }
            }));
            
            // 폼 초기화
            resetEmailForm();
            
            // Google Analytics 회원가입 이벤트 추적
            trackSignup('email_custom');
          } else {
            throw new Error('이메일 발송에 실패했습니다.');
          }
        }
              } else {
          // 로그인
          console.log('🔑 [DEBUG] Firebase 로그인 시도...', { platform });
          
          try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            console.log('✅ [DEBUG] Firebase 로그인 성공:', userCredential.user.uid);
          
          // 현재 사용자의 문서만 직접 조회
          let userData = null;
          try {
            const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
            if (userDoc.exists()) {
              userData = { uid: userCredential.user.uid, ...userDoc.data() };
              console.log('📄 [DEBUG] 사용자 데이터 조회 성공');
            }
          } catch (firestoreError) {
            console.error('❌ [DEBUG] Firestore 사용자 데이터 조회 실패:', {
              error: firestoreError,
              message: firestoreError.message,
              code: firestoreError.code,
              platform
            });
            // Firestore 에러는 무시하고 계속 진행
          }
          
          // OAuth 연동된 계정은 이메일 인증 건너뛰기
          const needsEmailVerification = isEmailVerificationRequired(userCredential.user, userData);
          
          console.log('✅ [DEBUG] 로그인 완료', {
            needsEmailVerification,
            emailVerified: userCredential.user.emailVerified,
            platform
          });
          
          if (needsEmailVerification) {
            console.log('ℹ️ [DEBUG] 이메일 인증 필요 - 랜딩페이지 상단 경고로 안내 예정');
          }
          
          // 로그인 시에도 정보 업데이트
          try {
            await saveUserToFirestore(userCredential.user, {
              email: email,
              loginMethod: 'email',
              platform: platform
            });
            console.log('✅ [DEBUG] 로그인 정보 Firestore 업데이트 완료');
          } catch (saveError) {
            console.error('❌ [DEBUG] Firestore 저장 실패:', {
              error: saveError,
              message: saveError.message,
              code: saveError.code,
              platform
            });
            // Firestore 저장 실패는 무시하고 로그인은 성공으로 처리
          }
          
          setAuthLoading(false);
          
          // Google Analytics 로그인 이벤트 추적
          try {
            trackLogin('email');
          } catch (trackError) {
            console.error('❌ [DEBUG] Analytics 추적 실패:', trackError);
            // Analytics 실패는 무시
          }
          
          console.log('🎉 [DEBUG] handleEmailAuth 성공 완료 - return true 실행');
          return true;
          
        } catch (loginError) {
          console.error('❌ [DEBUG] Firebase 로그인 에러 발생:', {
            code: loginError.code,
            message: loginError.message,
            name: loginError.name,
            fullError: loginError
          });
          throw loginError; // 에러를 다시 throw하여 외부 catch 블록에서 처리
        }
      }
      
      console.log('🎉 [DEBUG] handleEmailAuth 성공 완료 - return true 실행');
      return true;
    } catch (e) {
      console.error('💥 [DEBUG] handleCustomEmailAuth catch 블록 실행');
      setAuthLoading(false);
      
      // ✅ Step 3: 플랫폼별 에러 디버깅
      const platform = Capacitor.getPlatform();
      console.error(`❌ [DEBUG] [${platform}] 이메일 인증 실패:`, {
        code: e.code,
        message: e.message,
        name: e.name,
        stack: e.stack,
        fullError: e,
        errorString: String(e),
        isSignup,
        platform,
        timestamp: new Date().toISOString()
      });
      
      // Android에서 특별히 에러 내용 확인
      if (platform === 'android') {
        console.error('🤖 [Android] 전체 에러 객체:', JSON.stringify(e, null, 2));
        console.error('🤖 [Android] 에러 타입:', typeof e);
        console.error('🤖 [Android] 에러 키:', Object.keys(e || {}));
      }
      
      const errorMessage = getAuthErrorMessage(e.code || 'unknown', isSignup);
      console.error(`💥 [DEBUG] 이메일 인증 실패: ${isSignup ? '회원가입' : '로그인'} - ${errorMessage}`);
      
      // 로그인 시에만 에러 메시지 표시 (회원가입은 모달로 처리)
      if (!isSignup) {
        console.log('🔴 [DEBUG] 로그인 에러 메시지 설정:', errorMessage);
        setAuthError(errorMessage);
        console.log('🔴 [DEBUG] setAuthError 호출 완료');
      }
      
      console.log('💥 [DEBUG] handleEmailAuth 실패 완료 - return false 실행');
      return false;
    }
  };

  /**
   * 폼 입력값 초기화 함수
   */
  const resetEmailForm = () => {
    setEmail('');
    setPassword('');
    setIsSignup(false);
    setAuthError(''); // 에러 초기화
  };

  return {
    // 상태
    email,
    setEmail,
    password,
    setPassword,
    isSignup,
    setIsSignup,
    authLoading,
    setAuthLoading,  // 이 부분이 빠져있었음!
    isResending,
    authError,
    setAuthError,
    
    // 함수
    handleEmailAuth: handleCustomEmailAuth,
    resetEmailForm,
    resendCustomVerificationEmail
  };
}; 